import { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { FromUTCEpochToMonthDayString } from '../../../../utils';
import { isDateOfToday } from 'modules/Analytics/utils';
import { getWorkflowEmailsGraphData, resetWorkflowGraphData, resetWorkflowGraphDate } from 'modules/Automation/actions';
import { getQueryWorkflowId } from 'modules/EmailBrowser/utils';
import { ReactComponent as Rectangle } from '../../../../assets/svgs/Rectangle.svg';
import { ReactComponent as Triangle } from '../../../../assets/svgs/Triangle.svg';

const colors = ['rgba(255, 150, 45,1)'];
const workflowId = getQueryWorkflowId();

const FromUTCEpochToMonthDayHourString = (epoch: number) => {
  const date: Date = new Date(epoch);
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}  ${date.getUTCHours()}:00/${date.getUTCFullYear()}`;
};
const ToMonthDayLabels = (labels: Array<number>) => {
  return labels.map((epoch: any) => {
    return FromUTCEpochToMonthDayString(epoch);
  });
};

const ToHourlyLabels = (labels: number[]) => {
  return labels.map((epoch: any) => {
    return FromUTCEpochToMonthDayHourString(epoch);
  });
};

const formatXAxisLabels = (showHourly: boolean, labels: number[]) => {
  let xAxisLabels;
  if (showHourly) {
    xAxisLabels = ToHourlyLabels(labels);
  } else {
    xAxisLabels = ToMonthDayLabels(labels);
  }
  return xAxisLabels;
};

export const useGraphDataScaler = (gradients: any) => {
  const [isLastDateOfGraphToday, setIsLastDateOfGraphToday] = useState(false);
  const [plotData, setPlotData] = useState({
    pDatasets: [],
    xAxesDateLabels: [],
  });
  const [rawData, setRawData] = useState({});
  const [colorGradients, setColorGradients] = useState(gradients);

  const data = useSelector((state: RootState) => {
    return state.automation.workflowEmailGraph.data;
  }, shallowEqual);
  const loading = useSelector((state: RootState) => {
    return state.automation.workflowEmailGraph.loading;
  }, shallowEqual);
  const selectedDatesObjectValue = useSelector((state: RootState) => state.automation.workflowEmailGraph.selectedDateFilter.label);
  const filterStartDate = useSelector((state: RootState) => state.automation.workflowEmailGraph.selectedDateValue.startDate);
  const filterEndDate = useSelector((state: RootState) => state.automation.workflowEmailGraph.selectedDateValue.endDate);
  const formatData = (data: any, gradients: any) => {
    let pDatasets: any = [];
    let xAxesDateLabels: any = [];
    let legendLabels: any = [
      { name: 'Unique opens', icon: <Triangle /> },
      { name: 'Unique clicks', icon: <Rectangle /> },
    ];

    const epochsMap = new Map();

    let utcEpochs: Array<number> = Array.from(
      new Set(
        data.events_data.map((dataPoint: any) => {
          return dataPoint[0];
        }),
      ),
    );
    utcEpochs = utcEpochs?.sort((a: any, b: any) => a - b);

    const xAxesLabels: Array<number> = utcEpochs;
    setIsLastDateOfGraphToday(isDateOfToday(utcEpochs[utcEpochs.length - 1]));
    xAxesLabels?.forEach((epoch, index) => {
      epochsMap.set(epoch, index);
    });
    const yAxesDatasets: Array<any> = [];
    let clicks: Array<any> = new Array(xAxesLabels.length).fill(0, 0, xAxesLabels.length);
    let opens: Array<any> = new Array(xAxesLabels.length).fill(0, 0, xAxesLabels.length);

    let startIndex = 99999999;

    data?.events_data?.forEach((datapoint: Array<number>) => {
      const epoch: number = datapoint[0];
      const ind: number = epochsMap.get(epoch);
      clicks[ind] = datapoint[1];
      opens[ind] = datapoint[2];
      if (ind < startIndex) {
        startIndex = ind;
      }
    });

    clicks.fill(null, 0, startIndex);
    opens.fill(null, 0, startIndex);

    startIndex = 99999999;

    yAxesDatasets.push({ clicks, opens });

    let colorIndex = 0;
    pDatasets = yAxesDatasets?.reduce((accumulator, currentValue) => {
      accumulator.push({
        label: 'Clicks ' + currentValue.name,
        fill: '+1',
        backgroundColor: gradients[colorIndex],
        borderColor: colors[colorIndex],
        borderCapStyle: 'butt',
        borderDashOffset: 0.0,
        borderJoinStyle: 'round',
        pointBorderColor: colors[colorIndex],
        pointBackgroundColor: 'white',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHitRadius: 10,
        data: currentValue.clicks,
        pointStyle: 'rectRot',
        spanGaps: true,
        borderDash: [0.3, 3],
      });
      accumulator.push({
        label: 'Opens ' + currentValue.name,
        fill: false,
        borderColor: colors[colorIndex],
        borderCapStyle: 'butt',
        borderDashOffset: 0.0,
        borderJoinStyle: 'round',
        pointBorderColor: colors[colorIndex],
        pointBackgroundColor: colors[colorIndex],
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHitRadius: 10,
        data: currentValue.opens,
        pointStyle: 'triangle',
        spanGaps: true,
        borderDash: [0.3, 3],
      });
      colorIndex++;
      return accumulator;
    }, []);
    // showing time for the emails for 1 day and having more than 1 data points for the same day
    xAxesDateLabels = formatXAxisLabels(false, xAxesLabels);
    return {
      xAxesDateLabels,
      pDatasets,
      legendLabels,
    };
  };

  const getDataDashboard = () => {
    let object: any = {
      start_date: filterStartDate,
      end_date: filterEndDate,
      engagement_trend: true,
      workflow_id: getQueryWorkflowId(),
    };
    getWorkflowEmailsGraphData(object);
  };

  useEffect(() => {
    if (loading) {
      setPlotData({
        pDatasets: [],
        xAxesDateLabels: [],
      });
      setRawData({});
    }
  }, [loading]);

  useEffect(() => {
    if (data && Object.keys(data).length !== 0 && data.constructor === Object) {
      if (JSON.stringify(rawData) !== JSON.stringify(data)) {
        setRawData(data);
      }
    }
  }, [data]);

  useEffect(() => {
    if (loading) {
      setPlotData({
        pDatasets: [],
        xAxesDateLabels: [],
      });
    } else if (rawData && Object.keys(rawData).length !== 0 && colorGradients?.length > 0) {
      const formattedData = formatData(rawData, colorGradients);
      setPlotData(formattedData);
    }
  }, [rawData, colorGradients, loading]);

  useEffect(() => {
    if (selectedDatesObjectValue) {
      resetWorkflowGraphData();
      getDataDashboard();
    }
  }, [selectedDatesObjectValue]);

  useEffect(() => {
    return () => {
      resetWorkflowGraphData();
      resetWorkflowGraphDate();
    };
  }, []);

  return [plotData, isLastDateOfGraphToday, setColorGradients];
};
